import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Cancel as CancelIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';

const SubscriptionManagement = ({ userId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [userId]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await subscriptionService.getMySubscription();
      setSubscription(response);
    } catch (err) {
      // 404 means no subscription - this is normal
      if (err.response?.status === 404) {
        setSubscription(null);
        setError('');
      } else {
        setError(err.response?.data?.error || 'Failed to load subscription');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelAtPeriodEnd(true);
    setCancelDialogOpen(true);
    setError('');
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    try {
      setCancelling(true);
      setError('');
      await subscriptionService.cancelSubscription(subscription.id, cancelAtPeriodEnd);
      setSuccess('Subscription cancelled successfully!');
      handleCloseCancelDialog();
      loadSubscription();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CreditCardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              You don't have an active subscription yet.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              onClick={() => navigate('/subscribe')}
              size="large"
            >
              Browse Subscription Plans
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CreditCardIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6">Subscription Details</Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Plan
                </Typography>
                <Typography variant="h6">
                  {subscription.plan?.name || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Price
                </Typography>
                <Typography variant="h6">
                  ${subscription.plan?.price || 'N/A'} / {subscription.plan?.billing_period || 'month'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                  color={getStatusColor(subscription.status)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Period Start
                </Typography>
                <Typography variant="body1">
                  {new Date(subscription.current_period_start).toLocaleDateString()}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Period End
                </Typography>
                <Typography variant="body1">
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </Typography>
              </Box>

              {subscription.cancel_at_period_end && (
                <Box>
                  <Alert severity="warning">
                    Subscription will be cancelled at the end of the current period.
                  </Alert>
                </Box>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Usage Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  LLM Tokens: {(subscription.plan?.llm_tokens_limit || 0).toLocaleString()} / month
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  Voice Tokens: {(subscription.plan?.voice_tokens_limit || 0).toLocaleString()} / month
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  Max Users: {subscription.plan?.max_users || 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {subscription.status !== 'canceled' && user?.role === 'tpb_manager' && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleOpenCancelDialog}
              >
                Cancel Subscription
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to cancel your {subscription?.plan?.name || 'subscription'}?
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Cancellation Option</InputLabel>
            <Select
              value={cancelAtPeriodEnd}
              label="Cancellation Option"
              onChange={(e) => setCancelAtPeriodEnd(e.target.value)}
            >
              <MenuItem value={true}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">Cancel at Period End</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your subscription will remain active until {new Date(subscription?.current_period_end).toLocaleDateString()}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value={false}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">Cancel Immediately</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your subscription will be cancelled right now
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelling}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            color="error"
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={24} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionManagement;
